// ─────────────────────────────────────────────────────────────────────────────
// THE LOOKS A TENANT CAN BE SET TO.
//
// ── WHY THIS FILE EXISTS ─────────────────────────────────────────────────────
//
// A complete 20-token theme was already shipping, injected at the app root, and
// its own comment admitted the problem: "Inline styles still win where they
// exist — we only shift the baseline." There was a theme switch wired to almost
// nothing (43 `var(--…)` uses in the whole frontend, all of them in App.js,
// against 247 literal hexes and 8,334 inline style objects).
//
// So this is not a new mechanism. It is the mechanism that was already here,
// moved somewhere a second value set can be dropped into it and a test can read
// it.
//
// ── WHAT IS AND IS NOT THEMEABLE ─────────────────────────────────────────────
//
// Themeable: chrome, ground, brand, type, radii — everything a facility might
// reasonably want to look like itself.
//
// NOT themeable, and deliberately absent from every preset below: the four
// chart-check leg states. `chartCheckLegs.js` owns those, as literals, because
// a facility that can restyle NOT_CHECKED can configure the product to lie — a
// green tick over labs nobody looked at is the exact failure that file exists to
// prevent. `a-theme-cannot-restyle-a-finding.test.js` enforces the boundary.
//
// ── `serif` IS THE DISPLAY SLOT ──────────────────────────────────────────────
//
// The name is kept because 42 live `var(--ed-serif)` references already depend on
// it. It means "the display face", which need not be a serif: `ehr` and
// `clinical` both put a sans in it on purpose.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Every token name a preset must define, as the suffix after `--ed-`.
 *
 * A PRESET MISSING ONE IS A HALF-PAINTED SCREEN, not a slightly-off one: the
 * custom property falls back to whatever the previous theme left on :root, so the
 * failure reads as a rendering glitch rather than as a missing value.
 * `themePresets.test.js` asserts every preset defines exactly this set.
 */
export const TOKEN_KEYS = Object.freeze([
  // Ground and text.
  //
  // FIVE TEXT STEPS, NOT THREE, AND THE REASON IS COUNTED. The screens were written
  // with the full slate ramp inline — #0f172a, #334155, #475569, #64748b, #94a3b8 —
  // which is five. A token layer offering three forces two of those five to collapse
  // into one, and the pair that collapses is whichever the converter happened to pick.
  // `ink-3` and `muted-2` are the two steps that were already in use and had nowhere
  // to land, at 430 and 559 occurrences respectively.
  "bg", "canvas", "ink", "ink-2", "ink-3", "muted", "muted-2",
  // Lines and the states they take
  "line", "line-2", "line-hover", "control-hover", "scroll-thumb", "scroll-thumb-hover",
  "disabled",
  // Brand
  "brand", "brand-deep", "brand-soft", "brand-lift", "brand-edge", "brand-glow",
  // Application-level semantics (NOT the chart-check leg states).
  //
  // FOUR STEPS PER FAMILY, for the same reason. A status panel is a tinted ground, a
  // border on it, body text and a heading — `alert-soft` + `alert-line` + `alert` +
  // `alert-deep`. With only base and soft, every border and every heading in a red,
  // green or amber panel had to stay a literal, which is most of why a theme did not
  // visibly reach the app. `info` is a whole family that did not exist: the blue
  // notice panel is the commonest one on these screens and was 415 literals.
  "alert", "alert-deep", "alert-soft", "alert-line",
  "success", "success-deep", "success-soft", "success-line",
  "amber", "amber-deep", "amber-soft", "amber-line",
  "violet", "violet-deep", "violet-soft", "violet-line",
  // The navigation is a DARK GROUND and gets its own four, not brand/brand-deep: the
  // paper-form preset wants a ruled near-black bar where brand would give it a colour,
  // and `nav-ink` is the only text colour in the product that sits on a dark surface.
  "nav-from", "nav-to", "nav-accent", "nav-ink",
  "info", "info-deep", "info-soft", "info-line",
  // Depth
  "shadow",
  // Type
  "serif", "sans",
  // Radii
  "r-sm", "r-md", "r-lg", "r-xl", "r-pill",
]);

/**
 * The foreground/background pairs the baseline stylesheet actually puts together.
 *
 * DERIVED FROM USAGE, NOT INVENTED. Each entry below corresponds to a real rule
 * in `baselineCss()` — `.form-field label` is `muted` on `canvas`, `.sig-tab` over
 * `.table-row:hover` is `muted` on `line-2`, `.tag-chip` is `brand` on
 * `brand-soft`. A hand-written wish list would drift from the stylesheet the way
 * the old theme's comment drifted from the inline styles.
 */
export const CONTRAST_PAIRS = Object.freeze([
  { fg: "ink", bg: "canvas", where: "body text on a card" },
  { fg: "ink", bg: "bg", where: "body text on the page ground" },
  { fg: "ink-2", bg: "canvas", where: ".btn-secondary label" },
  { fg: "ink-2", bg: "bg", where: "secondary text on the page ground" },
  { fg: "ink-2", bg: "line-2", where: ".btn-ghost:hover label" },
  { fg: "muted", bg: "canvas", where: ".form-field label (12px) and .sig-tab" },
  { fg: "muted", bg: "bg", where: ".sig-tab on the page ground" },
  { fg: "muted", bg: "line-2", where: ".sig-tab over .table-row:hover" },
  { fg: "brand", bg: "canvas", where: "links and .tag-chip text on a card" },
  { fg: "brand", bg: "bg", where: "links on the page ground" },
  { fg: "brand", bg: "brand-soft", where: ".tag-chip" },
  // The chart check fills an OPEN resident row with brand-soft so twenty lines of
  // detail are visibly bound to the name above them, which puts all three text
  // weights on that surface.
  { fg: "ink", bg: "brand-soft", where: "an open chart-check row's heading" },
  { fg: "ink-2", bg: "brand-soft", where: "an open chart-check row's body text" },
  { fg: "muted", bg: "brand-soft", where: "an open chart-check row's quiet text" },
  { fg: "canvas", bg: "brand", where: ".btn-primary label" },
  { fg: "canvas", bg: "brand-deep", where: ".btn-primary:hover label" },
  { fg: "alert", bg: "alert-soft", where: "an error badge" },
  { fg: "success", bg: "success-soft", where: "a success badge" },
  { fg: "amber", bg: "amber-soft", where: "a warning badge" },
  { fg: "violet", bg: "canvas", where: "a secondary badge on a card" },

  // ── THE PAIRS THE INLINE STYLES WERE ALREADY MAKING ───────────────────────
  //
  // Added with the five-step ramp and the four-step families, and derived the same
  // way: each one is a combination the SCREENS put together, now that the screens
  // reference tokens instead of literals. A status panel is the shape that produces
  // most of them — tinted ground, heading in the deep step, body in the base step.
  //
  // SOME OF THESE FAIL ON THE DEFAULT PRESET AND ARE DECLARED AS DEBT, because the
  // default preset's values ARE the literals that were already on screen. Measuring
  // them for the first time does not make them new, and quietly darkening 559 pieces
  // of live text inside a mechanical conversion is how a refactor becomes a redesign
  // nobody reviewed. The debt list is where they are visible and the next commit's
  // business; `themePresets.test.js` refuses a debt entry that has been fixed, so
  // none of them can sit there as permission once somebody does the work.
  { fg: "ink-3", bg: "canvas", where: "third-step body text on a card" },
  { fg: "ink-3", bg: "bg", where: "third-step body text on the page ground" },
  { fg: "muted-2", bg: "canvas", where: "a timestamp or placeholder on a card" },
  { fg: "muted-2", bg: "bg", where: "a timestamp on the page ground" },
  { fg: "alert-deep", bg: "alert-soft", where: "the heading of an error panel" },
  { fg: "success-deep", bg: "success-soft", where: "the heading of a success panel" },
  { fg: "amber-deep", bg: "amber-soft", where: "the heading of a warning panel" },
  { fg: "violet", bg: "violet-soft", where: "a secondary panel's body text" },
  { fg: "violet-deep", bg: "violet-soft", where: "the heading of a secondary panel" },
  { fg: "info", bg: "info-soft", where: "a notice panel's body text" },
  { fg: "info", bg: "canvas", where: "an informational link or badge on a card" },
  { fg: "info-deep", bg: "info-soft", where: "the heading of a notice panel" },

  // ── THE NAVIGATION, WHICH IS THE ONLY DARK GROUND IN THE PRODUCT ──────────
  //
  // Every other pair above is dark-on-light. These are the inverse, and they were never
  // measured because the sidebar's two colours were literals in App.js rather than
  // tokens — so the most prominent surface in the app had no contrast gate on it at all.
  // Both ends of the gradient are checked: a value that passes against nav-from and
  // fails against nav-to is unreadable across half the bar.
  { fg: "nav-ink", bg: "nav-from", where: "sidebar and mobile-header labels, at the top of the gradient" },
  { fg: "nav-ink", bg: "nav-to", where: "the same labels, at the bottom of it" },
  { fg: "nav-accent", bg: "nav-from", where: "the logo's accent half and the view eyebrow" },
  { fg: "nav-accent", bg: "nav-to", where: "the same, at the other end" },
]);

/**
 * Pairs that are measured and reported but not gated.
 *
 * Measured-and-exempt and never-looked-at are different facts, and this is which
 * one it is. These are WCAG 1.4.11 boundary contrasts — a hairline border against
 * the surface it sits on. 3:1 is the stated target and essentially no design
 * system on the web meets it for a 1px card border (Tailwind's own border-gray-200
 * is about 1.2:1), so gating on it would mean an allowlist covering every preset,
 * which is permission wearing a test's clothes. Reported instead, so the number is
 * visible when someone wants to argue about it.
 *
 * `.btn-primary:disabled` USED TO BE THE ENTRY HERE, at 1.66:1 to 2.15:1 — white
 * on a mid-grey, with opacity 0.7 over the top. It leaned on 1.4.3's exemption for
 * inactive controls, which is true and was still the wrong answer for a button a
 * nurse has to read to know an action is unavailable. It is a light fill with muted
 * text now, which lands it in the GATED list above as `muted` on `line-2`.
 */
export const ADVISORY_PAIRS = Object.freeze([
  { fg: "line", bg: "canvas", where: ".card border against the card (WCAG 1.4.11, target 3:1)" },
  { fg: "line", bg: "bg", where: ".card border against the page ground (WCAG 1.4.11)" },
]);

const SANS_FALLBACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export const PRESETS = Object.freeze({
  // ── THE ONE THAT IS ACTUALLY ON SCREEN TODAY ───────────────────────────────
  //
  // `editorial` below says "the look shipping today" and that was true of the BASELINE
  // STYLESHEET only. The app's own screens were 9,450 inline colour literals from a
  // different palette entirely — navy #0f4c81, the slate ramp, Tailwind's semantic
  // red/green/amber/blue — so what a user saw was editorial's warm cream ground with
  // navy-and-slate components sitting on it. A mixture, not either look.
  //
  // THIS PRESET IS THAT PALETTE, EXACTLY, AND THAT IS WHAT MAKES THE CONVERSION
  // REVIEWABLE. Converting 9,450 literals to tokens can only be checked one way: the
  // default preset's value for each token is the literal it replaced, so under the
  // default the conversion renders pixel-for-pixel what it rendered before and any
  // visible difference is a mapping error rather than a judgement call. Every value
  // below was read out of the screens by `shippedPalette.js`, not chosen here.
  //
  // It is therefore DEFAULT_PRESET. A tenant who has never chosen a look still sees
  // exactly what they saw, and a tenant who picks one of the six designed looks now
  // gets it everywhere instead of on the chrome alone — which is the whole point.
  //
  // Two values are NOT from the screens, and both are the token layer having no
  // literal to copy: `r-*` (the inline styles used 4, 6, 8, 10 and 12px with no
  // ladder, so the four most common are recorded) and the three rgba() depths.
  standard: {
    key: "standard",
    label: "SurvAIHealth standard",
    description: "Navy brand, slate neutrals, Tailwind semantics. What the app looks like today — the default.",
    // No webfont: the shipping screens use the system stack, and loading one here
    // would be a visible change to the default look on the first paint.
    fonts: null,
    tokens: {
      "bg": "#f8fafc",
      "canvas": "#ffffff",
      "ink": "#0f172a",
      "ink-2": "#334155",
      "ink-3": "#475569",
      "muted": "#64748b",
      "muted-2": "#94a3b8",
      "line": "#e2e8f0",
      "line-2": "#f1f5f9",
      "line-hover": "#cbd5e1",
      "control-hover": "#94a3b8",
      "scroll-thumb": "#cbd5e1",
      "scroll-thumb-hover": "#94a3b8",
      "disabled": "#cbd5e1",
      "brand": "#0f4c81",
      "brand-deep": "#0c447c",
      "brand-soft": "#eff6ff",
      "brand-lift": "#0891b2",
      "brand-edge": "rgba(15,76,129,0.18)",
      "brand-glow": "rgba(8,145,178,0.25)",
      "alert": "#dc2626",
      "alert-deep": "#991b1b",
      "alert-soft": "#fef2f2",
      "alert-line": "#fecaca",
      "success": "#059669",
      "success-deep": "#166534",
      "success-soft": "#f0fdf4",
      "success-line": "#bbf7d0",
      // #b45309 rather than the #d97706 that appears beside it on these screens:
      // both are live, and only one of them is readable on #fffbeb. The gated pair
      // `amber on amber-soft` is the reason the choice is forced here rather than
      // left to whichever literal the converter met first.
      "amber": "#b45309",
      "amber-deep": "#92400e",
      "amber-soft": "#fffbeb",
      "amber-line": "#fde68a",
      "violet": "#7c3aed",
      "violet-deep": "#6d28d9",
      "violet-soft": "#f5f3ff",
      "violet-line": "#ddd6fe",
      "info": "#0369a1",
      "info-deep": "#1e40af",
      "info-soft": "#f0f9ff",
      "info-line": "#bfdbfe",
      "shadow": "rgba(15,23,42,0.12)",
      "serif": SANS_FALLBACK,
      "sans": SANS_FALLBACK,
      // ── THE NAVIGATION, WHICH REFUSED TO FOLLOW THE THEME ─────────────────
      // The desktop sidebar, the mobile header and the mobile drawer were all
      // hardcoded to `linear-gradient(#083e4a, #0b5566)` — editorial's brand-deep and
      // brand, written as literals. So the single most prominent surface on both
      // layouts stayed teal whichever look a tenant chose. Its own tokens rather than
      // brand/brand-deep because the nav is a dark ground with its own identity: the
      // paper-form preset wants a ruled near-black bar, not a coloured one.
      "nav-from": "#083e4a",
      "nav-to": "#0b5566",
      "nav-accent": "#38bdf8",
      "nav-ink": "#ffffff",
      "r-sm": "4px",
      "r-md": "6px",
      "r-lg": "8px",
      "r-xl": "12px",
      "r-pill": "999px",
    },
  },

  // ── THE ONE THAT WAS ALREADY LIVE IN THE BASELINE STYLESHEET ───────────────
  //
  // Lifted verbatim out of App.js so that moving the theme out of that file
  // changes nothing on screen. TWO VALUES ARE DELIBERATELY NOT VERBATIM, and
  // both are legibility fixes rather than design changes:
  //
  //   muted  #78869a -> #5d6979   3.14:1 -> 4.73:1 on its worst ground
  //   amber  #a05e0c -> #96590b   4.47:1 -> 4.91:1 on amber-soft
  //
  // `muted` is the colour of every `.form-field label` at 12px and every
  // inactive `.sig-tab`, so it was the most-read text in the app and the least
  // readable. The replacement holds the original's hue (214) and saturation
  // (13%) and only drops lightness, so it reads as the same grey-blue.
  // The other two failures found at the same time are recorded as debt in
  // themePresets.test.js, because fixing them WOULD be a visible change.
  editorial: {
    key: "editorial",
    label: "Editorial",
    // NO LONGER "the look shipping today", and the correction matters: it described the
    // baseline stylesheet, which is what this preset was lifted from, while the screens
    // themselves were a different palette inline. Calling it the shipping look is how a
    // reader concludes that choosing any other preset is the only change being made.
    description: "Warm cream ground, Fraunces display, teal brand. The designed editorial look.",
    fonts: "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Instrument+Sans:wght@400;500;600;700&display=swap",
    tokens: {
      "bg": "#f7f5f1",
      "canvas": "#ffffff",
      "ink": "#0f1a24",
      "ink-2": "#3b4a5a",
      "muted": "#5d6979",
      "line": "#e7e4dd",
      "line-2": "#efece5",
      "line-hover": "#d7d2c6",
      "control-hover": "#c8c3b5",
      "scroll-thumb": "#c8c3b5",
      "scroll-thumb-hover": "#a8a394",
      "disabled": "#a8b2bd",
      "brand": "#0b5566",
      "brand-deep": "#083e4a",
      "brand-soft": "#e6eef0",
      "brand-lift": "#2f9fb5",
      "brand-edge": "rgba(11,85,102,0.15)",
      "brand-glow": "rgba(11,85,102,0.08)",
      "alert": "#b3261e",
      "alert-soft": "#fce8e6",
      "success": "#2f6b4f",
      "success-soft": "#e6f0ea",
      "amber": "#96590b",
      "amber-soft": "#fbeed7",
      "violet": "#4b3a7a",
      "shadow": "rgba(15,26,36,0.12)",
      "serif": "'Fraunces', Georgia, serif",
      "sans": `'Instrument Sans', 'DM Sans', ${SANS_FALLBACK}`,
      // ── THE SLOTS THE INLINE LITERALS NEEDED ─────────────────────────────
      // Three text steps and four-step semantic families, because 9,450 inline
      // colours were using them and had nowhere to land. See shippedPalette.js.
      "ink-3": "#4a5a6b",
      "muted-2": "#69717d",
      "alert-deep": "#8c1d18",
      "alert-line": "#e8c4c0",
      "success-deep": "#1f4a37",
      "success-line": "#b9d3c6",
      "amber-deep": "#6e410a",
      "amber-line": "#e3cb9f",
      "violet-deep": "#382a5c",
      "violet-soft": "#eeebf6",
      "violet-line": "#c6bcde",
      "info": "#1a567f",
      "info-deep": "#123f5e",
      "info-soft": "#e8eff5",
      "info-line": "#bbd1e2",
      // ── THE NAVIGATION, WHICH REFUSED TO FOLLOW THE THEME ─────────────────
      // The desktop sidebar, the mobile header and the mobile drawer were all
      // hardcoded to `linear-gradient(#083e4a, #0b5566)` — editorial's brand-deep and
      // brand, written as literals. So the single most prominent surface on both
      // layouts stayed teal whichever look a tenant chose. Its own tokens rather than
      // brand/brand-deep because the nav is a dark ground with its own identity: the
      // paper-form preset wants a ruled near-black bar, not a coloured one.
      "nav-from": "#083e4a",
      "nav-to": "#0b5566",
      "nav-accent": "#8bc9d6",
      "nav-ink": "#ffffff",
      "r-sm": "8px",
      "r-md": "9px",
      "r-lg": "10px",
      "r-xl": "12px",
      "r-pill": "99px",
    },
  },

  // ── U.S. WEB DESIGN SYSTEM ─────────────────────────────────────────────────
  // The system most state .gov sites and CMS.gov run on. Square, high contrast,
  // heavy rules, Public Sans over Merriweather.
  federal: {
    key: "federal",
    label: "Federal",
    description: "USWDS lineage — square corners, heavy rules, Public Sans and Merriweather.",
    fonts: "https://fonts.googleapis.com/css2?family=Public+Sans:wght@400;600;700&family=Merriweather:wght@700&display=swap",
    tokens: {
      "bg": "#ffffff",
      "canvas": "#ffffff",
      "ink": "#1b1b1b",
      "ink-2": "#3d4551",
      "muted": "#565c65",
      "line": "#dfe1e2",
      "line-2": "#f0f0f0",
      "line-hover": "#a9aeb1",
      "control-hover": "#a9aeb1",
      "scroll-thumb": "#a9aeb1",
      "scroll-thumb-hover": "#71767a",
      "disabled": "#c9c9c9",
      "brand": "#005ea2",
      "brand-deep": "#1a4480",
      "brand-soft": "#e1f3f8",
      // Flat, not a gradient: brand-lift equal to brand turns .progress-fill's
      // linear-gradient into a solid. That is the one token that decides it.
      "brand-lift": "#005ea2",
      "brand-edge": "rgba(0,94,162,0.18)",
      "brand-glow": "rgba(36,145,255,0.25)",
      "alert": "#b50909",
      "alert-soft": "#f8dfe2",
      "success": "#154c21",
      "success-soft": "#ecf3ec",
      "amber": "#5c4809",
      "amber-soft": "#faf3d1",
      "violet": "#54278f",
      "shadow": "rgba(27,27,27,0.12)",
      "serif": "'Merriweather', Georgia, serif",
      "sans": `'Public Sans', ${SANS_FALLBACK}`,
      // ── THE SLOTS THE INLINE LITERALS NEEDED ─────────────────────────────
      // Three text steps and four-step semantic families, because 9,450 inline
      // colours were using them and had nowhere to land. See shippedPalette.js.
      "ink-3": "#4d5663",
      "muted-2": "#71767a",
      "alert-deep": "#7c0d05",
      "alert-line": "#f2bcb6",
      "success-deep": "#0c3d14",
      "success-line": "#bbdcbd",
      "amber-deep": "#453508",
      "amber-line": "#e9d9a0",
      "violet-deep": "#3f1f70",
      "violet-soft": "#f2f0fa",
      "violet-line": "#cbbfe6",
      "info": "#0d5e7a",
      "info-deep": "#09465c",
      "info-soft": "#e7f6f8",
      "info-line": "#a8dbe8",
      // ── THE NAVIGATION, WHICH REFUSED TO FOLLOW THE THEME ─────────────────
      // The desktop sidebar, the mobile header and the mobile drawer were all
      // hardcoded to `linear-gradient(#083e4a, #0b5566)` — editorial's brand-deep and
      // brand, written as literals. So the single most prominent surface on both
      // layouts stayed teal whichever look a tenant chose. Its own tokens rather than
      // brand/brand-deep because the nav is a dark ground with its own identity: the
      // paper-form preset wants a ruled near-black bar, not a coloured one.
      "nav-from": "#1a4480",
      "nav-to": "#005ea2",
      "nav-accent": "#b8d8f3",
      "nav-ink": "#ffffff",
      "r-sm": "2px",
      "r-md": "4px",
      "r-lg": "4px",
      "r-xl": "4px",
      "r-pill": "999px",
    },
  },

  // ── CMS CARE COMPARE ───────────────────────────────────────────────────────
  // Medicare.gov's consumer side: big type, airy, plain tables, one strong blue.
  compare: {
    key: "compare",
    label: "Care Compare",
    description: "CMS consumer lineage — large airy type, plain tables, Open Sans and Zilla Slab.",
    fonts: "https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&family=Zilla+Slab:wght@600;700&display=swap",
    tokens: {
      "bg": "#ffffff",
      "canvas": "#ffffff",
      "ink": "#212121",
      "ink-2": "#4b4f54",
      "muted": "#5b616b",
      "line": "#e4e4e4",
      "line-2": "#f7f7f7",
      "line-hover": "#d6d7d9",
      "control-hover": "#a6a6a6",
      "scroll-thumb": "#c6c6c6",
      "scroll-thumb-hover": "#8a8a8a",
      "disabled": "#c4c4c4",
      "brand": "#0b5ea8",
      "brand-deep": "#073e6f",
      "brand-soft": "#e8f1f9",
      "brand-lift": "#0b5ea8",
      "brand-edge": "rgba(11,94,168,0.18)",
      "brand-glow": "rgba(249,168,37,0.3)",
      "alert": "#b02a1f",
      "alert-soft": "#fbe9e7",
      "success": "#1c6b3a",
      "success-soft": "#e7f2ec",
      "amber": "#7a5c00",
      "amber-soft": "#fdf5d9",
      "violet": "#4a2f8f",
      "shadow": "rgba(33,33,33,0.12)",
      "serif": "'Zilla Slab', Georgia, serif",
      "sans": `'Open Sans', ${SANS_FALLBACK}`,
      // ── THE SLOTS THE INLINE LITERALS NEEDED ─────────────────────────────
      // Three text steps and four-step semantic families, because 9,450 inline
      // colours were using them and had nowhere to land. See shippedPalette.js.
      "ink-3": "#4b5157",
      "muted-2": "#71767a",
      "alert-deep": "#7f1d14",
      "alert-line": "#f1c3bd",
      "success-deep": "#14512b",
      "success-line": "#bcdcc6",
      "amber-deep": "#5c4500",
      "amber-line": "#ecdda0",
      "violet-deep": "#38236b",
      "violet-soft": "#f1eefa",
      "violet-line": "#cbc0e6",
      "info": "#0a607f",
      "info-deep": "#074760",
      "info-soft": "#e8f4f9",
      "info-line": "#a9d8e8",
      // ── THE NAVIGATION, WHICH REFUSED TO FOLLOW THE THEME ─────────────────
      // The desktop sidebar, the mobile header and the mobile drawer were all
      // hardcoded to `linear-gradient(#083e4a, #0b5566)` — editorial's brand-deep and
      // brand, written as literals. So the single most prominent surface on both
      // layouts stayed teal whichever look a tenant chose. Its own tokens rather than
      // brand/brand-deep because the nav is a dark ground with its own identity: the
      // paper-form preset wants a ruled near-black bar, not a coloured one.
      "nav-from": "#073e6f",
      "nav-to": "#0b5ea8",
      "nav-accent": "#b2dcea",
      "nav-ink": "#ffffff",
      "r-sm": "4px",
      "r-md": "6px",
      "r-lg": "6px",
      "r-xl": "4px",
      "r-pill": "999px",
    },
  },

  // ── SURVEY EXHIBIT ─────────────────────────────────────────────────────────
  // The CMS-2567 paper-form genre, which is what this product's output IS. Note
  // `line` is near-black on purpose: a form is ruled, not shaded. Every radius is
  // 0, the pill included — a square tag is correct here.
  //
  // It borrows the form's grammar and carries no agency marking: nothing in this
  // preset should be made to look like it was issued by HHS or CMS.
  exhibit: {
    key: "exhibit",
    label: "Survey exhibit",
    description: "Paper-form lineage — ruled black hairlines, Source Serif 4, zero radius. Prints in black and white.",
    fonts: "https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,600;8..60,700&family=Archivo+Narrow:wght@500;600;700&display=swap",
    tokens: {
      "bg": "#e8e6e1",
      "canvas": "#faf9f6",
      "ink": "#0d0d0d",
      "ink-2": "#3a3a3a",
      "muted": "#5a5a5a",
      "line": "#0d0d0d",
      "line-2": "#f2f0ea",
      "line-hover": "#8c1515",
      "control-hover": "#0d0d0d",
      "scroll-thumb": "#b9b6ae",
      "scroll-thumb-hover": "#8f8c84",
      "disabled": "#b9b6ae",
      "brand": "#0d0d0d",
      "brand-deep": "#8c1515",
      "brand-soft": "#f2f0ea",
      "brand-lift": "#0d0d0d",
      "brand-edge": "rgba(13,13,13,0.2)",
      "brand-glow": "rgba(140,21,21,0.25)",
      "alert": "#8c1515",
      "alert-soft": "#f6ece9",
      "success": "#1f4d2e",
      "success-soft": "#ecf1ed",
      "amber": "#6b4e00",
      "amber-soft": "#f7f0dc",
      "violet": "#3f2d6b",
      "shadow": "rgba(13,13,13,0.14)",
      "serif": "'Source Serif 4', Georgia, serif",
      "sans": "'Archivo Narrow', 'Arial Narrow', sans-serif",
      // ── THE SLOTS THE INLINE LITERALS NEEDED ─────────────────────────────
      // Three text steps and four-step semantic families, because 9,450 inline
      // colours were using them and had nowhere to land. See shippedPalette.js.
      "ink-3": "#4a4a4a",
      "muted-2": "#676767",
      "alert-deep": "#6b1010",
      "alert-line": "#dbbcb8",
      "success-deep": "#163a22",
      "success-line": "#c0cfc4",
      "amber-deep": "#4f3900",
      "amber-line": "#d8caa0",
      "violet-deep": "#2d1f4d",
      "violet-soft": "#efecf4",
      "violet-line": "#c3b9d6",
      "info": "#1b4a63",
      "info-deep": "#123445",
      "info-soft": "#eaeff2",
      "info-line": "#b7c9d4",
      // ── THE NAVIGATION, WHICH REFUSED TO FOLLOW THE THEME ─────────────────
      // The desktop sidebar, the mobile header and the mobile drawer were all
      // hardcoded to `linear-gradient(#083e4a, #0b5566)` — editorial's brand-deep and
      // brand, written as literals. So the single most prominent surface on both
      // layouts stayed teal whichever look a tenant chose. Its own tokens rather than
      // brand/brand-deep because the nav is a dark ground with its own identity: the
      // paper-form preset wants a ruled near-black bar, not a coloured one.
      "nav-from": "#0d0d0d",
      "nav-to": "#2b2b2b",
      "nav-accent": "#d9d6ce",
      "nav-ink": "#faf9f6",
      "r-sm": "0",
      "r-md": "0",
      "r-lg": "0",
      "r-xl": "0",
      "r-pill": "0",
    },
  },

  // ── EHR DENSITY ────────────────────────────────────────────────────────────
  // The genre the users already sit in all day. Near-square, graphite and teal,
  // IBM Plex in both slots. Density itself is a separate axis — see DENSITIES.
  ehr: {
    key: "ehr",
    label: "Clinical workstation",
    description: "EHR lineage — graphite and teal chrome, near-square, IBM Plex. Pairs with compact density.",
    fonts: "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap",
    tokens: {
      "bg": "#f4f6f7",
      "canvas": "#ffffff",
      "ink": "#1f2933",
      "ink-2": "#3d4a54",
      "muted": "#53616b",
      "line": "#e3e8eb",
      "line-2": "#eef2f4",
      "line-hover": "#cdd6db",
      "control-hover": "#a9b6bd",
      "scroll-thumb": "#cdd6db",
      "scroll-thumb-hover": "#a9b6bd",
      "disabled": "#b8c2c8",
      "brand": "#0f6b6b",
      "brand-deep": "#0b5151",
      "brand-soft": "#d9e5e5",
      "brand-lift": "#0f6b6b",
      "brand-edge": "rgba(15,107,107,0.18)",
      "brand-glow": "rgba(16,129,127,0.25)",
      "alert": "#8c2117",
      "alert-soft": "#fae8e5",
      "success": "#1b6340",
      "success-soft": "#e6efe9",
      "amber": "#6b5420",
      "amber-soft": "#f4f1ea",
      "violet": "#453a78",
      "shadow": "rgba(31,41,51,0.12)",
      "serif": `'IBM Plex Sans', ${SANS_FALLBACK}`,
      "sans": `'IBM Plex Sans', ${SANS_FALLBACK}`,
      // ── THE SLOTS THE INLINE LITERALS NEEDED ─────────────────────────────
      // Three text steps and four-step semantic families, because 9,450 inline
      // colours were using them and had nowhere to land. See shippedPalette.js.
      "ink-3": "#47545e",
      "muted-2": "#66727a",
      "alert-deep": "#6b190f",
      "alert-line": "#eac3bd",
      "success-deep": "#124a2f",
      "success-line": "#bad5c6",
      "amber-deep": "#4f3d16",
      "amber-line": "#d7ccb3",
      "violet-deep": "#342c5c",
      "violet-soft": "#ecebf3",
      "violet-line": "#c2bed9",
      "info": "#15607d",
      "info-deep": "#0f4659",
      "info-soft": "#e4eff3",
      "info-line": "#aecdd9",
      // ── THE NAVIGATION, WHICH REFUSED TO FOLLOW THE THEME ─────────────────
      // The desktop sidebar, the mobile header and the mobile drawer were all
      // hardcoded to `linear-gradient(#083e4a, #0b5566)` — editorial's brand-deep and
      // brand, written as literals. So the single most prominent surface on both
      // layouts stayed teal whichever look a tenant chose. Its own tokens rather than
      // brand/brand-deep because the nav is a dark ground with its own identity: the
      // paper-form preset wants a ruled near-black bar, not a coloured one.
      "nav-from": "#0b5151",
      "nav-to": "#0f6b6b",
      "nav-accent": "#b3e4e4",
      "nav-ink": "#ffffff",
      "r-sm": "2px",
      "r-md": "2px",
      "r-lg": "2px",
      "r-xl": "2px",
      "r-pill": "2px",
    },
  },

  // ── CLINICAL PRODUCT ───────────────────────────────────────────────────────
  // Derived from the brand navy #0f4c81 already used 557 times in this frontend.
  // The neutral ramp is pinned at the brand's own hue (208) instead of drifting to
  // 222 the way an off-the-shelf slate does, and the accent is the one warm value
  // that stays legible as text.
  clinical: {
    key: "clinical",
    label: "Clinical product",
    description: "Derived from the brand navy — neutral ramp pinned at hue 208, Archivo over Source Sans 3.",
    fonts: "https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700&family=Source+Sans+3:wght@400;600;700&display=swap",
    tokens: {
      "bg": "#f8fafc",
      "canvas": "#ffffff",
      "ink": "#132230",
      "ink-2": "#2d465c",
      "muted": "#43607a",
      "line": "#e2e8ee",
      "line-2": "#eef3f8",
      "line-hover": "#c9d4de",
      "control-hover": "#97a9ba",
      "scroll-thumb": "#c9d4de",
      "scroll-thumb-hover": "#97a9ba",
      "disabled": "#b4c2ce",
      "brand": "#0f4c81",
      "brand-deep": "#0a3a63",
      "brand-soft": "#e4ecf4",
      "brand-lift": "#0f4c81",
      "brand-edge": "rgba(15,76,129,0.18)",
      "brand-glow": "rgba(154,58,15,0.22)",
      "alert": "#9a3a0f",
      "alert-soft": "#fdf1ea",
      "success": "#1f5c42",
      "success-soft": "#e8f1ec",
      "amber": "#7d5312",
      "amber-soft": "#faf0dc",
      "violet": "#41377a",
      "shadow": "rgba(19,34,48,0.12)",
      "serif": `'Archivo', ${SANS_FALLBACK}`,
      "sans": `'Source Sans 3', ${SANS_FALLBACK}`,
      // ── THE SLOTS THE INLINE LITERALS NEEDED ─────────────────────────────
      // Three text steps and four-step semantic families, because 9,450 inline
      // colours were using them and had nowhere to land. See shippedPalette.js.
      "ink-3": "#3a536b",
      "muted-2": "#60758b",
      "alert-deep": "#732b0b",
      "alert-line": "#f0cbb9",
      "success-deep": "#164633",
      "success-line": "#bdd7c9",
      "amber-deep": "#5d3d0d",
      "amber-line": "#e2cda6",
      "violet-deep": "#322b5c",
      "violet-soft": "#edecf5",
      "violet-line": "#c3bfda",
      "info": "#0e5f86",
      "info-deep": "#0a4660",
      "info-soft": "#e5eff5",
      "info-line": "#afd0e2",
      // ── THE NAVIGATION, WHICH REFUSED TO FOLLOW THE THEME ─────────────────
      // The desktop sidebar, the mobile header and the mobile drawer were all
      // hardcoded to `linear-gradient(#083e4a, #0b5566)` — editorial's brand-deep and
      // brand, written as literals. So the single most prominent surface on both
      // layouts stayed teal whichever look a tenant chose. Its own tokens rather than
      // brand/brand-deep because the nav is a dark ground with its own identity: the
      // paper-form preset wants a ruled near-black bar, not a coloured one.
      "nav-from": "#0a3a63",
      "nav-to": "#0f4c81",
      "nav-accent": "#8ec2e8",
      "nav-ink": "#ffffff",
      "r-sm": "4px",
      "r-md": "4px",
      "r-lg": "6px",
      "r-xl": "6px",
      "r-pill": "999px",
    },
  },
});

/**
 * The second axis.
 *
 * The CMS and EHR directions differ mostly by DENSITY, not palette, so exposing it
 * separately gets two useful looks out of each skin rather than duplicating six
 * presets. It is also the axis a reviewer would actually move: the named risk on
 * the chart check is overwhelm, and "fewer pixels per row" and "a different blue"
 * are not the same lever.
 */
export const DENSITIES = Object.freeze({
  comfortable: { key: "comfortable", label: "Comfortable", scale: 1 },
  compact: { key: "compact", label: "Compact", scale: 0.78 },
});

// THE DEFAULT MOVED, AND THAT MOVE IS WHAT KEEPS THE CONVERSION HONEST.
//
// It was `editorial`, which was correct while the screens were literals: the baseline
// stylesheet was the only themed surface, and editorial WAS that stylesheet. Now that
// the screens reference tokens, the default decides what 9,450 converted sites render,
// and the only default under which "convert a literal to a token" changes nothing on
// screen is the preset whose values are those literals. So: `standard`.
//
// Pointing it at `editorial` instead would have shipped a redesign of every screen in
// the product inside a mechanical refactor, for every tenant at once, with no one
// having chosen it.
export const DEFAULT_PRESET = "standard";
export const DEFAULT_DENSITY = "comfortable";

/**
 * The preset for a key, falling back to the live look.
 *
 * AN UNKNOWN KEY IS THE DEFAULT, NEVER A BLANK THEME. A tenant row written by a
 * newer server, a typo, a null — none of those should produce a screen with no
 * colours on it, which is what returning undefined would do once `applyTheme`
 * starts writing custom properties from it.
 */
export function presetFor(key) {
  return PRESETS[key] || PRESETS[DEFAULT_PRESET];
}

export function densityFor(key) {
  return DENSITIES[key] || DENSITIES[DEFAULT_DENSITY];
}

/** Every preset, in the order a picker should offer them. */
export function presetList() {
  return Object.values(PRESETS).map(({ key, label, description }) => ({ key, label, description }));
}
